import {useWindowSize} from '@primer/react-brand'
import {useTheme} from '@primer/styled-react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {
  ProductAnnouncementModalTab,
  ProductAnnouncementModalTabVisual,
} from './../../ProductAnnouncement/ProductAnnouncement.types'
import {PauseIcon, PlayIcon} from './../PlayIcon/PlayIcon'

import Styles from './ProductAnnouncementModalVisual.module.css'

export interface ProductAnnouncementModalVisualProps {
  currentTabIndex: number
  visuals: Array<ProductAnnouncementModalTab['visual']>
  aria: {
    togglePlay: string
  }
}

const defaultProps: Partial<ProductAnnouncementModalVisualProps> = {}

const ProductAnnouncementModalVisual = (props: ProductAnnouncementModalVisualProps) => {
  const initializedProps: ProductAnnouncementModalVisualProps = {...defaultProps, ...props}
  const {currentTabIndex, visuals, aria} = initializedProps

  const [playingStates, setPlayingStates] = useState<boolean[]>([])
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([])

  const {resolvedColorScheme: colorScheme} = useTheme()
  const {isMedium: isDesktopView} = useWindowSize()

  const colorMode = useMemo(() => (['dark', 'night'].includes(colorScheme!) ? 'dark' : 'light'), [colorScheme])

  const getVisual = useCallback(
    (index: number) => {
      const visual = visuals[index]
      if (!visual) return null

      if ('desktop' in visual) {
        return isDesktopView ? visual.desktop : visual.mobile
      }

      return visual
    },
    [isDesktopView, visuals],
  )

  const getVisualSrc = useCallback(
    (src: ProductAnnouncementModalTabVisual['src']) => {
      if (typeof src === 'string') return src
      return colorMode === 'dark' ? src.dark : src.light
    },
    [colorMode],
  )

  const getVisualDOM = useCallback(
    (visual: ProductAnnouncementModalTabVisual, tabIndex: number) => {
      const offsetVars: React.CSSProperties = {
        ['--offset-x']: visual.offsetX || 'center',
        ['--offset-y']: visual.offsetY || 'center',
      }

      if (visual.type === 'image') {
        return <img src={getVisualSrc(visual.src)} alt={visual.alt || ''} loading="lazy" style={offsetVars} />
      }

      const isPlaying = playingStates[tabIndex] || false

      return (
        <>
          <video
            ref={ref => {
              videoRefs.current[tabIndex] = ref
            }}
            src={getVisualSrc(visual.src)}
            poster={visual.posterSrc}
            aria-label={visual.alt || ''}
            muted
            onPlay={() => {
              setPlayingStates(value => {
                const updatedValue = [...value]
                updatedValue[tabIndex] = true
                return updatedValue
              })
            }}
            onPause={() => {
              setPlayingStates(value => {
                const next = [...value]
                next[tabIndex] = false
                return next
              })
            }}
            style={offsetVars}
            // Firefox will make it focusable by default, even without `controls` present
            // cf. https://allyjs.io/data-tables/focusable.html
            tabIndex={-1}
          />

          <button
            aria-label={aria.togglePlay}
            onClick={() => {
              const video = videoRefs.current[tabIndex]
              if (video) {
                if (video.paused) {
                  try {
                    void video.play()
                  } catch {
                    // Autoplay might be blocked, do nothing
                  }
                } else {
                  video.pause()
                }
              }
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
        </>
      )
    },
    [aria, getVisualSrc, playingStates],
  )

  useEffect(() => {
    for (const [index, video] of videoRefs.current.entries()) {
      if (video) {
        if (index === currentTabIndex) {
          try {
            video.currentTime = 0
            void video.play()
          } catch {
            // Autoplay might be blocked, do nothing
          }
        } else {
          video.pause()
        }
      }
    }
  }, [currentTabIndex])

  return (
    <div className={Styles['ProductAnnouncementModalVisual']}>
      {visuals.map((_, index) => {
        const isActive = index === currentTabIndex
        const visual = getVisual(index)
        return (
          visual && (
            <div
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`visual-${index}`}
              className={clsx(
                Styles['ProductAnnouncementModalVisual__item'],
                isActive && Styles['ProductAnnouncementModalVisual__item--active'],
              )}
              // @ts-expect-error `inert` exists
              inert={!isActive ? 'true' : undefined}
            >
              {getVisualDOM(visual, index)}
            </div>
          )
        )
      })}
    </div>
  )
}

export default ProductAnnouncementModalVisual

try{ ProductAnnouncementModalVisual.displayName ||= 'ProductAnnouncementModalVisual' } catch {}